import React from "react";
import { Link } from "react-router-dom";
import {
  Languages,
  FileText,
  Mic,
  BookOpen,
  PhoneCall,
  Captions,
  Radio,
  Bell
} from "lucide-react";

const useCasesLeft = [
  {
    icon: Languages,
    title: "Multilingual Website & App Localization",
    description: "Translate key pages, product UI, FAQs, and help content so users understand the experience from the first click.",
    targetId: "media"
  },
  {
    icon: Mic,
    title: "Voice Notes to Text For Field Teams",
    description: "Convert voice notes into text for reporting, documentation, and faster decision-making.",
    targetId: "hr"
  },
  {
    icon: PhoneCall,
    title: "Customer Support Call Transcription",
    description: "Transcribe calls for coaching, auditing, and faster follow-ups —especially when conversations include multiple languages.",
    targetId: "telecom"
  },
  {
    icon: Radio,
    title: "IVR & Hotline Prompts In Local Languages",
    description: "Create clear, natural audio menus and automated announcements with Text-to-Speech.",
    targetId: "telecom"
  }
];

const useCasesRight = [
  {
    icon: FileText,
    title: "Multilingual Forms & Surveys",
    description: "Increase completion rates by translating instructions and questions into users' preferred languages.",
    targetId: "government"
  },
  {
    icon: BookOpen,
    title: "Training Materials & SOP Translation",
    description: "Localize internal documentation so teams follow processes consistently across locations",
    targetId: "education"
  },
  {
    icon: Captions,
    title: "Subtitles & Captions For Video",
    description: "Turn speech into text, then localize captions to expand reach and accessibility.",
    targetId: "media"
  },
  {
    icon: Bell,
    title: "Public Information & Community Alerts",
    description: "Share critical updates in text and audio formats that communities can understand quickly.",
    targetId: "health-care"
  }
];

const CommonUseCases = () => {
  return (
    <section className="w-full bg-[#1E6551] py-12 sm:py-16 text-white">
      <div
        className="max-w-9xl mx-auto sm:px-6 sm:px-10 lg:px-14"
        data-aos="fade-up"
      >
        {/* Header */}
        <div className="mb-12">
          <span className="text-[#48C59A] text-xs sm:text-sm font-bold tracking-widest uppercase block mb-2">
            USE CASES
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-white font-['Axiforma',sans-serif] tracking-tight">
            Common Use Cases
          </h2>
        </div>

        {/* 2-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 lg:gap-x-16">
          {/* Left Column */}
          <div className="flex flex-col">
            {useCasesLeft.map((item, idx) => {
              const Icon = item.icon;
              const isLast = idx === useCasesLeft.length - 1;
              return (
                <Link
                  key={idx}
                  to={`/industries-we-serve#${item.targetId}`}
                  className={`flex flex-col group transition-all duration-200 hover:opacity-95 ${!isLast ? "border-b border-white/10 pb-8 mb-8" : "pb-8 lg:pb-0 mb-8 lg:mb-0"}`}
                >
                  <div className="flex items-center gap-3.5 mb-2.5">
                    <div className="w-10 h-10 rounded-xl bg-white/10 text-[#48C59A] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Icon size={20} />
                    </div>
                    <h3 className="text-white font-bold text-lg sm:text-xl font-['Axiforma',sans-serif] group-hover:underline">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-emerald-100/80 text-sm sm:text-base leading-relaxed pl-13 sm:pl-[54px]">
                    {item.description}
                  </p>
                </Link>
              );
            })}
          </div>

          {/* Right Column */}
          <div className="flex flex-col">
            {useCasesRight.map((item, idx) => {
              const Icon = item.icon;
              const isLast = idx === useCasesRight.length - 1;
              return (
                <Link
                  key={idx}
                  to={`/industries-we-serve#${item.targetId}`}
                  className={`flex flex-col group transition-all duration-200 hover:opacity-95 ${!isLast ? "border-b border-white/10 pb-8 mb-8" : ""}`}
                >
                  <div className="flex items-center gap-3.5 mb-2.5">
                    <div className="w-10 h-10 rounded-xl bg-white/10 text-[#48C59A] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Icon size={20} />
                    </div>
                    <h3 className="text-white font-bold text-lg sm:text-xl font-['Axiforma',sans-serif] group-hover:underline">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-emerald-100/80 text-sm sm:text-base leading-relaxed pl-13 sm:pl-[54px]">
                    {item.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommonUseCases;
